import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Dashboard.module.css";
import useClientsApi from "../../../hooks/useClientsApi";
import useNotification from "../../../hooks/useNotification";
import {
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Button,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import Cancel from "@mui/icons-material/Cancel";
import NotificationSnackbar from "../../../components/NotificationSnackbar";
import SearchIcon from "@mui/icons-material/Search";
import { sanitizeInput } from "../../../utils/inputSanitizer";

export default function Clients() {
  const navigate = useNavigate();
  const notificationApi = useNotification();
  const { notification, hideNotification, showNotification } = notificationApi;
  const { loading, clients, fetchClients, createClient } =
    useClientsApi(showNotification);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    contactNumber: "",
    clientId: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("Disable");

  React.useEffect(() => {
    fetchClients();
  }, []);

  // Handle form input
  // const handleChange = (e) => {
  //   const value =
  //     e.target.name === "isActive" ? e.target.value === "true" : e.target.value;
  //   setForm({ ...form, [e.target.name]: value });
  // };

  const handleDetailsChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleChange = (e) => {
    let value = e.target.value;

    if (e.target.name === "isActive") {
      value = value === "true";
    } else if (e.target.name === "name") {
      value = sanitizeInput(value); // sanitize only the name field
    }

    setForm({ ...form, [e.target.name]: value });
  };

  const handleEdit = (client) => {
    navigate(`/superadmin/clients/${client.id}/edit`);
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      cellClassName: "email-cell",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const isActive = params.value === 1;
        return (
          <Chip
            label={isActive ? "Enable" : "Disable"}
            color={isActive ? "success" : "error"}
            size="small"
            sx={{
              fontSize: "0.775rem",
              width: 60, // fixed width
              justifyContent: "center", // center the label
              textTransform: "capitalize", // optional: make label look cleaner
            }}
          />
        );
      },
    },
    {
      field: "apiKey",
      headerName: "Unique Code",
      renderCell: (params) => (
        <span style={{ textTransform: "none" }}>{params.value}</span>
      ),
      width: 300,
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          style={{ color: "var(--color-icon)" }}
          title="Edit"
          onClick={() => handleEdit(params.row)}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  // Sorting and filtering
  const filteredClients = clients.filter(
    (client) =>
      (client.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (client.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (client.contactNumber || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (client.country || "").toLowerCase().includes(search.toLowerCase())
  );

  const sortedClients = [...filteredClients].sort((a, b) => {
    if (a[sortBy] < b[sortBy]) return sortDir === "asc" ? -1 : 1;
    if (a[sortBy] > b[sortBy]) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Only SuperAdmin can add/edit clients
  const userRole = localStorage.getItem("role");
  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 className={styles.dashboardTitle}>CLIENTS</h1>
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            minWidth: 0,
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            labelPlacement="start"
            control={
              <Switch
                checked={statusFilter === "Enable"}
                onChange={() => {
                  setStatusFilter(
                    statusFilter === "Enable" ? "Disable" : "Enable"
                  );
                  statusFilter === "Enable" ? fetchClients() : fetchClients(0);
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#43a047", // light green
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#43a047",
                  },
                }}
              />
            }
            label={
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "var(--color-header)",
                }}
              >
                Show Disabled
              </span>
            }
          />
          <div
            style={{
              position: "relative",
              minWidth: 0,
              flex: "1 1 120px",
              width: "100%",
            }}
          >
            <input
              type="text"
              // placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "8px 24px 8px 40px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: "0.98rem",
                width: "100%",
                height: 40,
                boxSizing: "border-box",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "55%",
                transform: "translateY(-50%)",
                color: "#888",
                pointerEvents: "none",
              }}
            >
              <SearchIcon />
            </span>
          </div>
          {userRole === "SuperAdmin" && (
            <div style={{ position: "relative", display: "inline-block" }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                onClick={() => {
                  setShowCreateForm(true);
                  setEditingId(null);
                  setForm({
                    name: "",
                    email: "",
                    clientId: "",
                    isActive: true,
                  });
                }}
                sx={{
                  width: "100%",
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-secondary)" },
                }}
              >
                Add Client
              </Button>
            </div>
          )}
          {userRole === "SuperAdmin" && showCreateForm && (
            <Dialog
              open={showCreateForm}
              onClose={() => setShowCreateForm(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  minWidth: 300,
                  maxWidth: 450,
                  p: 1,
                  position: "relative",
                },
              }}
            >
              {/* Absolute positioned close button */}
              <IconButton
                aria-label="close"
                onClick={() => setShowCreateForm(false)}
                size="small"
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "var(--color-close-icon)",
                  zIndex: 1,
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
              <DialogTitle sx={{ pr: 4 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="var(--color-primary)"
                >
                  {editingId
                    ? "Update Client"
                    : "Add a New Client".toUpperCase()}
                </Typography>
              </DialogTitle>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const payload = {
                      name: form.name,
                      email: form.email,
                      contactNumber: form.contactNumber,
                      status: 1, // default to active
                    };
                    const result = await createClient(payload);
                    if (result.success) {
                      setForm({
                        name: "",
                        email: "",
                        contactNumber: "",
                        isActive: true,
                      });
                      setShowCreateForm(false);
                      fetchClients(); // Refresh the list
                    }
                  } catch (err) {
                    console.error("Error creating client:", err);
                  }
                }}
              >
                <DialogContent sx={{ pt: 1 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    inputProps={{ maxLength: 30 }}
                    sx={{ mb: 2 }}
                    variant="outlined"
                    size="small"
                    required
                    autoFocus
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    inputProps={{ maxLength: 50 }}
                    value={form.email}
                    onChange={handleChange}
                    sx={{ mb: 2 }}
                    variant="outlined"
                    size="small"
                    required
                  />
                  <TextField
                    fullWidth
                    label="Telephone"
                    name="contactNumber"
                    required
                    value={form.contactNumber}
                    onChange={(e) =>
                      handleDetailsChange(
                        "contactNumber",
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    sx={{ mb: 2 }}
                    inputProps={{ maxLength: 15 }}
                  />
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "var(--color-primary)",
                      "&:hover": { backgroundColor: "var(--color-secondary)" },
                    }}
                  >
                    {editingId ? "Update" : "Create Client"}
                  </Button>
                </DialogContent>
              </form>
            </Dialog>
          )}
        </div>
      </div>

      <Paper sx={{ height: 580, width: "100%", borderRadius: 2, boxShadow: 2,cursor: 'pointer' }}>
        <DataGrid
          rows={sortedClients}
          onClick={(params) => handleEdit(params.row)}
          columns={columns}
          rowHeight={36}
          loading={loading}
          // sortModel={[{ field: sortBy, sort: sortDir }]}
          // onSortModelChange={(model) => {
          //   if (model.length) {
          //     setSortBy(model[0].field);
          //     setSortDir(model[0].sort);
          //   }
          // }}
          pageSizeOptions={[15, 30, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 15, page: 0 } },
          }}
          disableRowSelectionOnClick
          onRowDoubleClick={(params) => handleEdit(params.row)}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
        />
      </Paper>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
